import { BORDERS, COLORS } from '@alto/design-library-tokens';
import { format, formatISO } from 'date-fns';
import React, { useState } from 'react';
import { View } from 'react-native';
import { type DateData, getInitialSelectedDate } from '../../../utils';
import { ActionSheetContext, DatePickerActionSheet } from '../../action-sheet';
import { Border } from '../../borders';
import { Pressable } from '../../buttons';
import { type CalendarProps } from '../../calendar';
import { MdPadding, Row } from '../../containers';
import { Icon } from '../../icon';
import { MdSpacing, XsSpacing } from '../../separators';
import { Body, Caption } from '../../typography';
import { InputLabelRow } from './InputText/InputLabelRow';
import { InputLoader } from './shared/InputLoader';

export type InputDatePickerProps = {
  readonly label?: string;
  readonly disabled?: boolean;
  readonly loading?: boolean;
  readonly error?: string;
  readonly required?: boolean;
  readonly optional?: boolean;
  readonly placeholder: string;
  readonly modalTitle?: string;
  readonly onDayPress?: (date: DateData) => void;
  readonly onDaySelect?: (date: DateData) => void;
  readonly children?: React.ReactNode;
} & Pick<CalendarProps, 'minDate' | 'maxDate' | 'disabledDates' | 'initialSelectedDate'>;

export const InputDatePicker = ({
  /**
   * Date string should be in ISO format without hours, minutes and second. (yyyy-MM-dd)
   * eg: "2022-09-21"
   */
  initialSelectedDate,
  /**
   * Array of Date strings should be in ISO format without hours, minutes and second. (yyyy-MM-dd)
   * eg: ["2022-09-21", "2022-09-22"]
   */
  disabledDates,
  disabled,
  loading,
  label,
  error,
  required,
  optional,
  placeholder,
  modalTitle = 'Select a Date',
  children,
  /**
   * Callback when pressing a date in calendar, even if disabled.
   */
  onDayPress,
  /**
   * Callback when pressing DatePickerActionSheet's primary CTA to save date selection
   */
  onDaySelect,
  minDate = formatISO(new Date(), { representation: 'date' }),
  maxDate,
}: InputDatePickerProps) => {
  /*
   * When using this component we use react-native-calendars that under the hood uses date-fns
   * Be extra careful when mixing date-fns and Date
   * When the Date constructor is used with yyyy-MM-dd without the timezone, it sets the timezone to GMT
   * format from date-fns uses local en-US. If mixed with Date, will lead to day changes
   *
   * Avoid: new Date("2022-09-21")
   * Use: new Date(2022, 9, 21)
   *
   * More info and examples: https://codesandbox.io/s/date-tz-less-iso-format-gotchas-zcvr2o
   */
  const { goToPreviousActionSheet, setActiveActionSheet } = React.useContext(ActionSheetContext);
  const [selectedDate, setSelectedDate] = useState<DateData>(getInitialSelectedDate(initialSelectedDate));

  const handleSelectedDate = (date: DateData) => {
    setSelectedDate(date);
    goToPreviousActionSheet();
    if (onDaySelect) {
      onDaySelect(date);
    }
  };

  const handleDayPress = (date: DateData) => {
    if (onDayPress) {
      onDayPress(date);
    }
  };

  const handleInputPressed = () => {
    setActiveActionSheet(
      <DatePickerActionSheet
        onDayPress={handleDayPress}
        onDaySelect={handleSelectedDate}
        disabledDates={disabledDates}
        modalTitle={modalTitle}
        initialSelectedDate={initialSelectedDate}
        selectedDate={selectedDate}
        minDate={minDate}
        maxDate={maxDate}
      >
        {children}
      </DatePickerActionSheet>,
    );
  };

  return (
    <View>
      {!!label && (
        <InputLabelRow
          label={label}
          error={error}
          required={required}
          optional={optional}
        />
      )}
      <Pressable
        opacity={false}
        disabled={disabled || loading}
        onPress={handleInputPressed}
        backgroundColor={disabled ? COLORS.BACKGROUND_COLORS.GREY_LIGHTER : COLORS.BACKGROUND_COLORS.PRIMARY_LIGHTEST}
        hoverBackgroundColor={COLORS.BACKGROUND_COLORS.PRIMARY_LIGHTER}
        pressedBackgroundColor={COLORS.BACKGROUND_COLORS.PRIMARY_LIGHTER}
        borderRadius={BORDERS.RADIUS.LG}
      >
        <Border
          radius="LG"
          color={error ? COLORS.BORDER_COLORS.DANGER : COLORS.BORDER_COLORS.LIGHT}
        >
          <MdPadding>
            <Row
              flexShrink={1}
              flexGrow={1}
              centerVertically={!loading}
              center={loading}
              wrap={false}
            >
              {loading ? (
                <InputLoader />
              ) : (
                <>
                  <Icon
                    name="calendar-small"
                    type={error ? 'danger' : undefined}
                  />
                  <XsSpacing />
                  <Row
                    flexGrow={1}
                    flexShrink={1}
                  >
                    <Body>{selectedDate ? format(selectedDate.timestamp, 'EEEE, MMMM do') : placeholder}</Body>
                  </Row>
                  <MdSpacing />
                  <Icon
                    name="chevrondown-small"
                    type="grey"
                  />
                </>
              )}
            </Row>
          </MdPadding>
        </Border>
      </Pressable>
      {!!error && (
        <>
          <XsSpacing />
          <Caption color={COLORS.TEXT_COLORS.DANGER}>{error}</Caption>
        </>
      )}
    </View>
  );
};
